/**
 * AdminTopbar — sticky topbar met breadcrumbs, globale zoek, quick actions,
 * notifications en user-menu.
 */
import React, { useState } from "react";
import { Menu, Search, Bell, LogOut } from "lucide-react";
import AdminBreadcrumbs from "./AdminBreadcrumbs";
import QuickActionsMenu from "./QuickActionsMenu";
import { KEY_TIP } from "./GlobalSearchDialog";
import { useAuth } from "../../../context/AuthContext";
import { useReminderAlerts } from "../ReminderAlertProvider";

export default function AdminTopbar({
  crumbs, onNavigate, onOpenSearch, onOpenMobileMenu, onLogout,
  onQuickAction,
}) {
  const { user } = useAuth();
  const { openCount, overdueCount, openModal } = useReminderAlerts();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-30 h-14 flex items-center gap-3 px-4 sm:px-6 bg-white/85 backdrop-blur-xl border-b border-slate-200"
      data-testid="admin-topbar"
    >
      {/* Mobile menu button */}
      <button
        type="button"
        onClick={onOpenMobileMenu}
        className="lg:hidden p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
        aria-label="Menu openen"
        data-testid="admin-mobile-menu-toggle"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Breadcrumbs */}
      <div className="flex-1 min-w-0 hidden sm:block">
        <AdminBreadcrumbs crumbs={crumbs} onNavigate={onNavigate} />
      </div>

      {/* Search trigger */}
      <button
        type="button"
        onClick={onOpenSearch}
        className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-md border border-slate-200 transition-colors duration-150 w-9 sm:w-64 justify-center sm:justify-start"
        data-testid="global-search-trigger"
        aria-label="Zoek"
      >
        <Search className="w-4 h-4 flex-shrink-0" />
        <span className="hidden sm:inline text-sm truncate">Zoek klanten, facturen, tracking…</span>
        <span className="ml-auto hidden sm:flex gap-1 items-center">
          <kbd className="text-[10px] font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-400">{KEY_TIP}</kbd>
          <kbd className="text-[10px] font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-400">K</kbd>
        </span>
      </button>

      {/* Notification bell */}
      <button
        type="button"
        onClick={openModal}
        className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
        aria-label="Meldingen"
        data-testid="topbar-notifications"
      >
        <Bell className="w-5 h-5" />
        {openCount > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${
              overdueCount > 0 ? "bg-red-600 text-white animate-pulse" : "bg-amber-500 text-white"
            }`}
          >
            {openCount > 99 ? "99+" : openCount}
          </span>
        )}
      </button>

      {/* Quick Actions */}
      <QuickActionsMenu onAction={onQuickAction} />

      {/* User menu — kort */}
      <div className="relative hidden sm:block">
        <button
          type="button"
          onClick={() => setUserMenuOpen((v) => !v)}
          className="flex items-center gap-2 p-1.5 hover:bg-slate-100 rounded-md transition-colors"
          data-testid="topbar-user-menu-trigger"
        >
          <div className="w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center">
            {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
          </div>
        </button>
        {userMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-20"
              onClick={() => setUserMenuOpen(false)}
              aria-hidden
            />
            <div
              className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-[0_10px_40px_rgba(0,0,0,0.12)] overflow-hidden z-30"
              data-testid="topbar-user-menu"
            >
              <div className="px-3 py-2.5 border-b border-slate-100">
                <div className="text-sm font-semibold text-slate-900 truncate">{user?.name || "—"}</div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              </div>
              <button
                type="button"
                onClick={() => { setUserMenuOpen(false); onLogout?.(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="topbar-logout"
              >
                <LogOut className="w-3.5 h-3.5" /> Uitloggen
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
